import { useState, useEffect } from "react"
import { Box, Text, Spinner, SimpleGrid } from "@chakra-ui/react"
import axios from "axios"
import { neumorph } from "./neumorph"
import { usePeriod } from "./PeriodContext"

const formatCurrency = (val) => {
  const num = parseFloat(val) || 0
  if (num >= 1_000_000) return `R ${(num / 1_000_000).toFixed(1)}M`
  if (num >= 1_000) return `R ${(num / 1_000).toFixed(0)}k`
  return `R ${num.toFixed(0)}`
}

const formatNum = (val) => {
  const num = parseInt(val) || 0
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}k`
  return num.toLocaleString()
}

const KpiTile = ({ label, value, sub, color = neumorph.textPrimary, loading }) => (
  <Box sx={{ ...neumorph.raisedSm, p: 4, minH: "92px" }}>
    <Text fontSize="2xs" color={neumorph.textMuted} textTransform="uppercase" letterSpacing="0.05em" fontWeight="600">
      {label}
    </Text>
    {loading ? (
      <Spinner size="sm" color={neumorph.accentTeal} mt={2} />
    ) : (
      <>
        <Text fontSize="xl" fontWeight="700" color={color} mt={1} lineHeight="1.2">
          {value}
        </Text>
        {sub && (
          <Text fontSize="2xs" color={neumorph.textMuted} mt={0.5}>
            {sub}
          </Text>
        )}
      </>
    )}
  </Box>
)

const KpiHeader = ({ supplier, retailer }) => {
  const { startDate, endDate } = usePeriod()
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(null)
  const [dormant, setDormant] = useState(null)

  useEffect(() => {
    if (!supplier || !retailer) return
    const controller = new AbortController()

    const fetchAll = async () => {
      setLoading(true)
      try {
        const userid = localStorage.getItem("userid")
        // allSettled: a slow / failed dormant call must not blank the whole KPI strip (2026-10-07).
        const [execSettled, dormantSettled] = await Promise.allSettled([
          axios.post(
            `${process.env.REACT_APP_API_URL}/insights/executive-summary`,
            { supplier, retailer, userid, startDate, endDate },
            { signal: controller.signal }
          ),
          axios.post(
            `${process.env.REACT_APP_API_URL}/insights/dormant-stock/kpi`,
            { supplier, retailer, userid, daysThreshold: 90 },
            { signal: controller.signal }
          ),
        ])
        if (controller.signal.aborted) return
        const execRes = execSettled.status === "fulfilled" ? execSettled.value : null
        const dormantRes = dormantSettled.status === "fulfilled" ? dormantSettled.value : null
        setData(execRes?.data?.status === 1 ? execRes.data.data?.kpis || null : null)
        setDormant(dormantRes?.data?.status === 1 ? dormantRes.data.data || null : null)
      } catch (err) {
        if (!axios.isCancel(err)) console.error("KpiHeader fetch error:", err)
      } finally {
        // a cancelled request (retailer / supplier switched) must not end the NEW request\'s loading state
        if (!controller.signal.aborted) setLoading(false)
      }
    }
    fetchAll()
    return () => controller.abort()
  }, [supplier, retailer, startDate, endDate])

  const k = data || {}
  const d = dormant || {}
  const gpColor =
    k.gp_percent >= 25
      ? neumorph.accentGreen
      : k.gp_percent >= 15
      ? neumorph.accentTeal
      : neumorph.accentRed

  return (
    <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={3} mb={4}>
      <KpiTile
        label="Revenue"
        value={formatCurrency(k.total_revenue)}
        sub={`${formatNum(k.total_units)} units`}
        color={neumorph.accentTeal}
        loading={loading}
      />
      <KpiTile
        label="Cost"
        value={formatCurrency(k.total_cost)}
        sub="Cost of sales"
        color={neumorph.textPrimary}
        loading={loading}
      />
      <KpiTile
        label="GP %"
        value={`${(k.gp_percent || 0).toFixed(1)}%`}
        sub={formatCurrency(k.gross_profit) + " profit"}
        color={gpColor}
        loading={loading}
      />
      <KpiTile
        label="Dormant Value"
        value={formatCurrency(d.total_dormant_value)}
        sub={`${formatNum(d.dormant_article_count)} articles`}
        color={parseFloat(d.total_dormant_value) > 10000 ? neumorph.accentOrange : neumorph.textPrimary}
        loading={loading}
      />
      <KpiTile
        label="SOH Value"
        value={formatCurrency(k.total_soh_value)}
        sub={`${formatNum(k.total_soh_qty)} units in stores`}
        color={neumorph.textPrimary}
        loading={loading}
      />
      <KpiTile
        label="Reach"
        value={`${formatNum(k.active_stores)} stores`}
        sub={`${k.active_articles || 0} articles selling`}
        color={neumorph.accentGreen}
        loading={loading}
      />
    </SimpleGrid>
  )
}

export default KpiHeader

// Re-export formatters for use by other revamp components
export { formatCurrency, formatNum }
